import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import Dialog from '../ui/Dialog';
import PeoplePicker from './PeoplePicker';
import * as S from '../specimens/styles';

/*
 * A new conversation: one person (POST /chat/dms: the conversation with them,
 * made once, opened again if it exists) or a named group of several
 * (POST /chat/channels). `onOpened(channelId)` opens it.
 */
export default function NewConversationDialog({ onClose, onOpened }) {
  const { t } = useTranslation(['chat', 'common']);
  const [group, setGroup] = useState(false);
  const [title, setTitle] = useState('');
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const openDm = (person) => {
    setBusy(true);
    setError('');
    api.post('/chat/dms', { user_id: person.user_id })
      .then((r) => onOpened(r.data.channel_id))
      .catch((e) => setError(errorText(e)))
      .finally(() => setBusy(false));
  };

  const createGroup = () => {
    setBusy(true);
    setError('');
    api.post('/chat/channels', { title: title.trim(), user_ids: chosen.map((p) => p.user_id) })
      .then((r) => onOpened(r.data.channel_id))
      .catch((e) => setError(errorText(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog title={t('newConversation')} onClose={onClose} testId="chat-new" width={460}>
      <div role="radiogroup" aria-label={t('newConversation')} style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[false, true].map((isGroup) => (
          <button key={String(isGroup)} type="button" role="radio" aria-checked={group === isGroup}
            onClick={() => setGroup(isGroup)} data-testid={isGroup ? 'chat-new-group' : 'chat-new-direct'}
            style={{ ...S.ghostBtn, ...(group === isGroup ? { background: 'var(--c-primary)', color: 'var(--c-on-accent)' } : {}) }}>
            {isGroup ? t('aGroup') : t('onePerson')}
          </button>
        ))}
      </div>
      {group && (
        <label style={{ ...S.label, marginBottom: 12 }}>
          {t('groupName')}
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} style={S.input}
            data-testid="chat-group-title" />
        </label>
      )}
      <PeoplePicker multiple={group} chosen={chosen}
        onPick={(p) => (group ? setChosen((c) => [...c, p]) : openDm(p))}
        onRemove={(p) => setChosen((c) => c.filter((x) => x.user_id !== p.user_id))} />
      {error && <p role="alert" style={S.error}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button type="button" style={S.ghostBtn} onClick={onClose}>{t('cancel')}</button>
        {group && (
          <button type="button" style={S.btn()} disabled={busy || !title.trim()} onClick={createGroup}
            data-testid="chat-group-create">{t('createGroup')}</button>
        )}
      </div>
    </Dialog>
  );
}
